<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="modelValue" class="modal-overlay" @click.self="close">
        <div class="detail-modal" :class="[sizeClass, { 'is-loading': loading }]">
          <!-- Header -->
          <div class="modal-header">
            <div class="header-left">
              <div class="header-icon" :class="iconClass">
                <vue-feather :type="icon" size="20" />
              </div>
              <div class="header-info">
                <h3 class="modal-title">{{ title }}</h3>
                <p v-if="subtitle" class="modal-subtitle">{{ subtitle }}</p>
              </div>
            </div>
            <div class="header-actions">
              <button 
                v-if="showCopy" 
                @click="copyContent" 
                class="header-btn"
                v-tooltip="'Copy content'"
              >
                <vue-feather type="copy" size="16" />
              </button>
              <button 
                v-if="showPrint" 
                @click="printContent" 
                class="header-btn"
                v-tooltip="'Print'"
              >
                <vue-feather type="printer" size="16" />
              </button>
              <button 
                v-if="onEdit" 
                @click="onEdit" 
                class="header-btn btn-primary"
                v-tooltip="'Edit'"
              >
                <vue-feather type="edit-2" size="16" />
              </button>
              <button @click="close" class="header-btn btn-close" v-tooltip="'Close'">
                <vue-feather type="x" size="18" />
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="modal-loading">
            <div class="spinner"></div>
            <p>Loading details...</p>
          </div>

          <!-- Content -->
          <div v-else class="modal-body" ref="contentRef">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div v-if="$slots.footer || showFooter" class="modal-footer">
            <slot name="footer">
              <div class="footer-info">
                <span v-if="createdAt" class="footer-meta">
                  <vue-feather type="calendar" size="14" />
                  Created {{ formatDate(createdAt) }}
                </span>
                <span v-if="updatedAt" class="footer-meta">
                  <vue-feather type="clock" size="14" />
                  Updated {{ formatDate(updatedAt) }}
                </span>
              </div>
              <div class="footer-actions">
                <div v-if="itemId" class="item-id-section" @click="copyId" v-tooltip="'Click to copy ID'">
                  <vue-feather type="hash" size="14" />
                  <span class="item-id-text">{{ itemId }}</span>
                  <vue-feather type="copy" size="12" class="copy-icon" />
                </div>
                <button @click="close" class="btn btn-secondary">Close</button>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref } from 'vue'
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'DetailViewModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: 'file-text'
    },
    iconClass: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    loading: {
      type: Boolean,
      default: false
    },
    showCopy: {
      type: Boolean,
      default: true
    },
    showPrint: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    createdAt: {
      type: String,
      default: ''
    },
    updatedAt: {
      type: String,
      default: ''
    },
    onEdit: {
      type: Function,
      default: null
    },
    itemId: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'close'],
  setup(props, { emit }) {
    const { toast } = useNotifications()
    const contentRef = ref(null)

    const sizeClass = {
      sm: 'modal-sm',
      md: 'modal-md',
      lg: 'modal-lg',
      xl: 'modal-xl',
      full: 'modal-full'
    }[props.size]

    const close = () => {
      emit('update:modelValue', false)
      emit('close')
    }

    const copyContent = async () => {
      try {
        const content = contentRef.value?.innerText || ''
        await navigator.clipboard.writeText(content)
        toast.success('Copied to clipboard!')
      } catch (error) {
        toast.error('Failed to copy content')
      }
    }

    const copyId = async () => {
      try {
        await navigator.clipboard.writeText(props.itemId)
        toast.success('ID copied to clipboard!')
      } catch (error) {
        toast.error('Failed to copy ID')
      }
    }

    const printContent = () => {
      window.print()
    }

    const formatDate = (date) => {
      if (!date) return ''
      const d = new Date(date)
      const now = new Date()
      const diffMs = now - d
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'just now'
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      })
    }

    return {
      contentRef,
      sizeClass,
      close,
      copyContent,
      copyId,
      printContent,
      formatDate
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
}

.detail-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  width: 100%;
  animation: slideUp 0.3s ease-out;
}

.modal-sm { max-width: 400px; }
.modal-md { max-width: 600px; }
.modal-lg { max-width: 800px; }
.modal-xl { max-width: 1200px; }
.modal-full { max-width: 95vw; }

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  flex-shrink: 0;
}

.header-icon.icon-question {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.header-icon.icon-note {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.header-icon.icon-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.header-info {
  flex: 1;
  min-width: 0;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.25rem 0 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.header-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.header-btn:hover {
  background: #e5e7eb;
  color: #374151;
  transform: translateY(-1px);
}

.header-btn.btn-primary {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
}

.header-btn.btn-primary:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
}

.header-btn.btn-close {
  background: transparent;
}

.header-btn.btn-close:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Loading */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  gap: 1rem;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.footer-meta {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #6b7280;
}

.footer-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.item-id-section {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.item-id-section:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
  color: #374151;
}

.item-id-text {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-icon {
  opacity: 0.5;
  transition: opacity 0.2s;
}

.item-id-section:hover .copy-icon {
  opacity: 1;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .detail-modal,
.modal-leave-active .detail-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .detail-modal,
.modal-leave-to .detail-modal {
  transform: translateY(20px) scale(0.95);
  opacity: 0;
}

/* Dark Mode */
:global(body.dark-mode) .detail-modal {
  background: #1e293b;
}

:global(body.dark-mode) .modal-header {
  border-bottom-color: #334155;
}

:global(body.dark-mode) .modal-title {
  color: #f3f4f6;
}

:global(body.dark-mode) .modal-subtitle {
  color: #9ca3af;
}

:global(body.dark-mode) .header-btn {
  background: #334155;
  color: #9ca3af;
}

:global(body.dark-mode) .header-btn:hover {
  background: #475569;
  color: #e5e7eb;
}

:global(body.dark-mode) .header-btn.btn-close:hover {
  background: #7f1d1d;
  color: #fca5a5;
}

:global(body.dark-mode) .modal-loading {
  color: #9ca3af;
}

:global(body.dark-mode) .spinner {
  border-color: #334155;
  border-top-color: #818cf8;
}

:global(body.dark-mode) .modal-body::-webkit-scrollbar-track {
  background: #0f172a;
}

:global(body.dark-mode) .modal-body::-webkit-scrollbar-thumb {
  background: #475569;
}

:global(body.dark-mode) .modal-body::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

:global(body.dark-mode) .modal-footer {
  border-top-color: #334155;
}

:global(body.dark-mode) .footer-meta {
  color: #9ca3af;
}

:global(body.dark-mode) .btn-secondary {
  background: #334155;
  color: #e5e7eb;
}

:global(body.dark-mode) .btn-secondary:hover {
  background: #475569;
}

:global(body.dark-mode) .item-id-section {
  background: #334155;
  color: #9ca3af;
}

:global(body.dark-mode) .item-id-section:hover {
  background: #475569;
  border-color: #64748b;
  color: #e5e7eb;
}

/* Print Styles */
@media print {
  .modal-overlay {
    background: white;
    position: static;
  }
  
  .detail-modal {
    box-shadow: none;
    max-height: none;
  }
  
  .modal-header,
  .modal-footer {
    display: none;
  }
  
  .modal-body {
    padding: 0;
  }
}
</style>
